/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronDown, ChevronUp, Upload, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultEvent = () => ({
  id: `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  enabled: true,
  delay_seconds: 4,
  date: '',
  time: '',
  image_url: '',
  notes: '',
  title_fr: '', title_en: '', title_pt: '',
  description_fr: '', description_en: '', description_pt: '',
  cta_text_fr: 'Réserver', cta_text_en: 'Book', cta_text_pt: 'Reservar',
});

/**
 * PopupEventsManager — CRUD list of event popups.
 * Persists via axios.put(`${API}/settings`, {...settings, popup_events: [...]}).
 */
const PopupEventsManager = ({ siteSettings, setSiteSettings }) => {
  const events = siteSettings?.popup_events || [];
  const [expandedId, setExpandedId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const persist = (updatedList) => {
    const updated = { ...siteSettings, popup_events: updatedList };
    setSiteSettings(updated);
    axios.put(`${API}/settings`, updated).then(() => toast.success('Sauvegardé')).catch(() => toast.error('Erreur'));
  };

  const addEvent = () => {
    const e = defaultEvent();
    const next = [...events, e];
    persist(next);
    setExpandedId(e.id);
  };

  const removeEvent = (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    persist(events.filter((e) => e.id !== id));
  };

  const updateEvent = (id, patch) => {
    persist(events.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  return (
    <div className="bg-[#121212] border border-white/10 p-4 space-y-3" data-testid="popup-events-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-white text-sm font-medium">Popups événements</h4>
          <p className="text-white/50 text-xs">Créez plusieurs événements (Brunch, Nuit du jazz, Menu Saint-Valentin…). Un seul popup s'affiche à la fois (le premier activé dont la date est future).</p>
        </div>
        <Button size="sm" onClick={addEvent} className="bg-[#d4af37] text-black hover:bg-[#d4af37]/90 h-8 px-3" data-testid="add-popup-event">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un événement
        </Button>
      </div>

      {events.length === 0 ? (
        <p className="text-white/40 text-xs italic pt-2">Aucun événement créé. Cliquez sur « Ajouter un événement ».</p>
      ) : (
        <div className="space-y-2">
          {events.map((e, idx) => {
            const isOpen = expandedId === e.id;
            const displayTitle = e.title_fr || e.title_en || e.title_pt || `Événement ${idx + 1}`;
            return (
              <div key={e.id} className={`border rounded ${e.enabled ? 'border-[#d4af37]/30 bg-black/40' : 'border-white/10 bg-black/20 opacity-70'}`} data-testid={`popup-event-${e.id}`}>
                {/* Row header */}
                <div className="flex items-center gap-2 p-2.5">
                  <label className="flex items-center gap-1.5 cursor-pointer" onClick={(ev) => ev.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!e.enabled}
                      onChange={(ev) => updateEvent(e.id, { enabled: ev.target.checked })}
                      className="w-4 h-4 accent-[#d4af37]"
                      data-testid={`popup-event-enabled-${e.id}`}
                    />
                  </label>
                  <button
                    onClick={() => setExpandedId(isOpen ? null : e.id)}
                    className="flex-1 min-w-0 text-left flex items-center gap-2"
                    data-testid={`popup-event-toggle-${e.id}`}
                  >
                    <span className="text-white text-sm font-medium truncate">{displayTitle}</span>
                    {e.date && <span className="text-[#d4af37] text-[11px] shrink-0">{e.date}{e.time ? ` · ${e.time}` : ''}</span>}
                    <span className={`ml-auto shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${e.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/40'}`}>
                      {e.enabled ? 'Actif' : 'Inactif'}
                    </span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                  </button>
                  <button
                    onClick={() => removeEvent(e.id)}
                    className="p-1.5 text-white/40 hover:text-red-400 transition"
                    title="Supprimer"
                    data-testid={`popup-event-delete-${e.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Expanded editor */}
                {isOpen && (
                  <div className="border-t border-white/5 p-3 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1 uppercase">Date</label>
                        <Input type="date" value={e.date || ''} onChange={(ev) => updateEvent(e.id, { date: ev.target.value })} className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-date-${e.id}`} />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1 uppercase">Heure</label>
                        <Input type="time" value={e.time || ''} onChange={(ev) => updateEvent(e.id, { time: ev.target.value })} className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-time-${e.id}`} />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1 uppercase">Délai (s)</label>
                        <Input type="number" min="0" max="60" value={e.delay_seconds ?? 4} onChange={(ev) => updateEvent(e.id, { delay_seconds: parseInt(ev.target.value) || 0 })} className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-delay-${e.id}`} />
                      </div>
                      <div>
                        <label className="text-white/50 text-[10px] block mb-1 uppercase">Note réservation</label>
                        <Input value={e.notes || ''} onChange={(ev) => updateEvent(e.id, { notes: ev.target.value })} placeholder="ex: Brunch" className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-notes-${e.id}`} />
                      </div>
                    </div>

                    <div>
                      <label className="text-white/50 text-[10px] block mb-1 uppercase">Média (image ou vidéo)</label>
                      <div className="flex gap-2 flex-wrap items-start">
                        <div className="flex-1 min-w-[240px] space-y-1">
                          <Input value={e.image_url || ''} onChange={(ev) => updateEvent(e.id, { image_url: ev.target.value })} placeholder="URL ou téléverser ci-contre…" className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-image-${e.id}`} />
                          <p className="text-white/30 text-[10px]">Formats acceptés : jpg, png, gif, webp, mp4, webm, mov (max 10 Mo). Les vidéos jouent automatiquement (muet, en boucle).</p>
                        </div>
                        <label className="cursor-pointer bg-white/5 border border-white/20 text-white text-xs h-8 px-3 rounded flex items-center gap-1.5 hover:bg-white/10 transition" data-testid={`evt-upload-btn-${e.id}`}>
                          {uploadingId === e.id ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…</>) : (<><Upload className="w-3.5 h-3.5" /> Téléverser</>)}
                          <input
                            type="file"
                            accept="image/*,video/*"
                            className="hidden"
                            disabled={uploadingId === e.id}
                            onChange={async (ev) => {
                              const file = ev.target.files?.[0];
                              if (!file) return;
                              if (file.size > 10 * 1024 * 1024) {
                                toast.error('Fichier trop volumineux (max 10 Mo)');
                                ev.target.value = '';
                                return;
                              }
                              setUploadingId(e.id);
                              try {
                                const fd = new FormData();
                                fd.append('file', file);
                                const r = await axios.post(`${API}/upload`, fd, {
                                  headers: { 'Content-Type': 'multipart/form-data' },
                                });
                                updateEvent(e.id, { image_url: r.data.url });
                                toast.success('Fichier téléversé');
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Erreur téléversement');
                              } finally {
                                setUploadingId(null);
                                ev.target.value = '';
                              }
                            }}
                          />
                        </label>
                      </div>
                      {e.image_url && (
                        <div className="mt-2 w-full max-w-[280px] h-32 bg-black/40 rounded overflow-hidden border border-white/10" data-testid={`evt-media-preview-${e.id}`}>
                          {(() => {
                            const url = e.image_url;
                            const isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
                            const src = url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
                            if (isVideo) {
                              return <video src={src} muted loop autoPlay playsInline className="w-full h-full object-cover" />;
                            }
                            return <img src={src} alt="preview" className="w-full h-full object-cover" onError={(evt) => { evt.currentTarget.style.opacity = '0.3'; }} />;
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {['fr', 'en', 'pt'].map((lng) => (
                        <div key={`t-${lng}`}>
                          <label className="text-white/50 text-[10px] block mb-1 uppercase">Titre {lng}</label>
                          <Input value={e[`title_${lng}`] || ''} onChange={(ev) => updateEvent(e.id, { [`title_${lng}`]: ev.target.value })} className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-title-${lng}-${e.id}`} />
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {['fr', 'en', 'pt'].map((lng) => (
                        <div key={`d-${lng}`}>
                          <label className="text-white/50 text-[10px] block mb-1 uppercase">Description {lng}</label>
                          <Textarea value={e[`description_${lng}`] || ''} onChange={(ev) => updateEvent(e.id, { [`description_${lng}`]: ev.target.value })} rows={3} className="bg-transparent border-white/20 text-white text-xs resize-none" data-testid={`evt-desc-${lng}-${e.id}`} />
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {['fr', 'en', 'pt'].map((lng) => (
                        <div key={`c-${lng}`}>
                          <label className="text-white/50 text-[10px] block mb-1 uppercase">Bouton {lng}</label>
                          <Input value={e[`cta_text_${lng}`] || ''} onChange={(ev) => updateEvent(e.id, { [`cta_text_${lng}`]: ev.target.value })} className="bg-transparent border-white/20 text-white text-xs h-8" data-testid={`evt-cta-${lng}-${e.id}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-white/30 text-[11px]">💡 Le popup s'affiche une fois par session par événement — si vous changez l'événement, les utilisateurs le verront à nouveau.</p>
    </div>
  );
};

export default PopupEventsManager;
